/**
 * Quality Triage — the landing tab: Jess's decision queue, worklist-first.
 *
 * Lanes (review 3ofroc round 2): "Waiting on your call" (high/critical),
 * "Open cases where the next step is ours", "Waiting on someone else" and
 * "Watching", each with a plain-language subtitle. Items waiting on a named
 * person are still the reader's own (the owner, waiting filter round): they
 * stay here in their own lane, quieter than the ones needing a decision and
 * not counted with them, and the filter on the left narrows the page to
 * them (or to one person, or to the items someone has answered on). "Answers
 * for you" lists each answer not yet marked seen (proposal 1). Every row shows severity,
 * source badge, signal count, age, and a pulsing NEW-SIGNAL indicator when
 * Merlin touched the thread since it was last read here. A row click opens
 * the thread's split workspace at its own URL (Phase 2). The latest daily
 * brief renders as a strip up top.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ageLabel,
  hasUnseenUpdate,
  seenSnapshot,
  severityEdge,
} from '../../components/portal/threadTriageView';
import {
  SOURCE_TAG_LABEL,
  THREAD_SEVERITY_LABEL,
  signalCountLabel,
} from '../../components/portal/investigationsView';
import { signalsReportsApi, type SignalReport } from '../../services/signalsReports';
import type { DoorThreadRead } from '../../services/qcpInvestigations';
import { caseColumns, checklistProgress } from './qualityTriageView';
import { caseWorkspacePath } from './qualityWorkspaceView';
import { isReopened, routedAwayThreads, triageList } from './qualityTriageView';
import { fromTab, threadWorkspacePath } from './qualityWorkspaceView';
import { useQualityTriage } from './qualityTriageContext';
import { trackEvent } from '../../services/portalTracking';
import { daysOverdue, untilDay, waitingOn } from './waitingOnView';
import { openAskWait, waitingWho } from './askWaitView';
import {
  attachDataRequest,
  markDataRequestSeen,
  type DoorDataRequestRead,
} from '../../services/qcpDataRequests';
import { AnswersForYou } from '../../components/quality/AnswersForYou';
import { FoundAnswers } from '../../components/quality/FoundAnswers';
import { undeliveredCount } from './deliveryView';
import { asksForCase, asksForThread, waitingOnNames } from './qualityWorkspaceView';
import { NOT_DELIVERED_WORDS, answeredWord, askWord, waitWord, type StateWord } from './stateWords';
import StateWordPill from './StateWordPill';
import FilterChip from './FilterChip';
import CaseListRow from './CaseListRow';
import {
  namesInclude,
  peopleWaitedOn,
  readListFilter,
  withListFilter,
  type ListFilter,
} from './listFilter';
import {
  noticeForThread,
  respondedLine,
  type ItemResponse,
} from './respondedView';
import { shortDate } from '../../components/portal/threadTriageView';
import { latestNoteLine } from '../../components/portal/noteBoxView';
import { SkipToItems } from './SkipToItems';
import { DOOR_ITEM_SELECTOR, focusFirst } from './doorFocus';
import { itemTargetAttr } from './itemNav';
import { sameName } from './deliveryView';
import { waitingTargets, type NudgeTarget } from './nudgeView';
import { useNudge } from './useNudge';
import { NudgePerson, NudgeRow, NudgedChip } from './NudgeControls';

/** Each unread answer's Read button, in list order. */
const ANSWER_READ_SELECTOR = '[data-testid="answer-row"] [data-answer-read]';
import { ThreadRefLabel } from './ThreadRefLabel';
import styles from './QualityTriagePages.module.css';

/** "📝 Jess Finlaw: Called Dave, no answer" — the item's newest note. */
function NoteLine({ thread }: { thread: DoorThreadRead }) {
  const line = latestNoteLine(thread.latest_note, thread.note_count ?? 0);
  return line ? (
    <span className={styles.rowNote} data-testid="row-note">
      📝 {line}
    </span>
  ) : null;
}

/** "✓ Cliff Platt responded 2h ago · answered “Credit ruling …”" under a row. */
function RespondedLine({ response }: { response: ItemResponse }) {
  return (
    <span className={styles.respondedLine} data-testid="row-responded">
      ✓ {respondedLine(response)}
      {response.question ? ` · answered “${response.question}”` : response.via === 'email' ? '' : ' on this item'}
    </span>
  );
}

function ThreadRow({
  thread,
  unseen,
  onOpen,
  response = null,
}: {
  thread: DoorThreadRead;
  unseen: boolean;
  onOpen: (thread: DoorThreadRead) => void;
  response?: ItemResponse | null;
}) {
  const signals = signalCountLabel(thread.signal_ids.length);
  const reopened = isReopened(thread);
  // Compact (proposal 4): title, one line of summary, the newest note, the
  // badges, the number and Open. Why it came back lives on the item page.
  return (
    <button
      type="button"
      className={`${styles.row} ${styles.rowCompact} ${response ? styles.rowResponded : ''}`}
      onClick={() => onOpen(thread)}
      data-testid="triage-card"
      data-door-item={thread.thread_no}
      // One recorder step per item opened, keyed by the item, instead of a
      // step per title (kanban 68b23ba8).
      data-action="qcp_item_open"
      {...{ [itemTargetAttr(thread.thread_no)]: thread.thread_no }}
    >
      <span className={`${styles.sev} ${styles[`sev_${severityEdge(thread.severity)}`]}`}>
        {THREAD_SEVERITY_LABEL[thread.severity] ?? thread.severity}
      </span>
      <span className={styles.rowBody}>
        <span className={styles.rowTitle}>{thread.title}</span>
        {thread.summary && <span className={styles.rowSub}>{thread.summary}</span>}
        <NoteLine thread={thread} />
        {response && <RespondedLine response={response} />}
        <span className={styles.chips}>
          {reopened && <span className={styles.chipReopened}>↩ Reopened</span>}
          {unseen && (
            <span className={styles.chipNew}>
              ● New since you looked · {ageLabel(thread.updated_at)}
            </span>
          )}
          <span
            className={`${styles.chip} ${
              thread.source_tag === 'sales_site' ? styles.chipConfirmed : styles.chipPossible
            }`}
          >
            {SOURCE_TAG_LABEL[thread.source_tag] ?? thread.source_tag}
          </span>
          {signals && <span className={styles.chip}>{signals}</span>}
          <span className={`${styles.chip} ${styles.chipAge}`}>
            {ageLabel(thread.created_at)} in queue
          </span>
          {/* The whole card opens the item; this says so. */}
          <span className={styles.rowOpen} aria-hidden="true">
            Open ▸
          </span>
        </span>
      </span>
      <span className={styles.rowRight}>
        <ThreadRefLabel thread={thread} />
      </span>
    </button>
  );
}

/**
 * One of my items waiting on a named person: who, since when, until when,
 * overdue in red; quieter than the rows that need a decision. When the
 * person has responded, the row says so in green instead.
 */
function WaitingRow({
  thread,
  now,
  onOpen,
  response = null,
  requests = [],
}: {
  thread: DoorThreadRead;
  now: Date;
  onOpen: (thread: DoorThreadRead) => void;
  response?: ItemResponse | null;
  /** The door's asks: an item with an open one waits on whoever was asked. */
  requests?: DoorDataRequestRead[];
}) {
  const w = waitingOn(thread);
  if (!w) {
    const asked = openAskWait(thread, requests);
    return asked ? (
      <AskedRow
        thread={thread}
        asked={asked.who}
        since={asked.since}
        word={askWord(asksForThread(requests, thread), now)}
        onOpen={onOpen}
      />
    ) : null;
  }
  const late = daysOverdue(w, now);
  const since = shortDate(w.since);
  // Its state in words (proposal 10): answered (unread until the reader
  // acknowledges it), overdue, or how long it has waited.
  const word = response
    ? answeredWord(
        w.state === 'answered' ||
          requests.some((r) => r.id === response.requestId && r.answer_unseen),
      )
    : waitWord({ since: w.since, due: w.until, dueWord: 'until' }, now);
  const tone = response ? styles.rowResponded : late > 0 ? styles.rowOverdue : styles.rowMuted;
  return (
    <button
      type="button"
      className={`${styles.row} ${tone}`}
      onClick={() => onOpen(thread)}
      data-testid="waiting-row"
      data-door-item={thread.thread_no}
      data-action="qcp_item_open"
      {...{ [itemTargetAttr(thread.thread_no)]: thread.thread_no }}
    >
      <span
        className={`${styles.sev} ${
          response ? styles.sev_responded : late > 0 ? styles.sev_overdue : styles.sev_waiting
        }`}
      >
        {response ? 'Answered' : late > 0 ? 'Overdue' : 'Waiting'}
      </span>
      <span className={styles.rowBody}>
        <span className={styles.rowTitle}>{thread.title}</span>
        <span className={styles.waitingLine}>
          Waiting on <strong>{w.who}</strong>
          {since && ` since ${since}`}
          {w.until
            ? late > 0
              ? ` · was due ${untilDay(w.until)}`
              : ` · until ${untilDay(w.until)}`
            : ' · no date set'}{' '}
          <StateWordPill word={word} />
        </span>
        {/* Overdue is said once, in the pill ("⏰ Overdue 2 days · chase"). */}
        {response && <RespondedLine response={response} />}
        {w.note && <span className={styles.rowSub}>{w.note}</span>}
        <NoteLine thread={thread} />
        <span className={styles.chips}>
          <span className={styles.chip}>
            {THREAD_SEVERITY_LABEL[thread.severity] ?? thread.severity}
          </span>
          <span className={`${styles.chip} ${styles.chipAge}`}>
            {ageLabel(thread.created_at)} since it came in
          </span>
          <NudgedChip thread={thread} />
        </span>
      </span>
      <span className={styles.rowRight}>
        <ThreadRefLabel thread={thread} />
      </span>
    </button>
  );
}

/**
 * One of my items with an open ask out (UX round 2026-10-06): who was asked
 * and since when, quiet like the other waits. It leaves this lane once the
 * ask is answered.
 */
function AskedRow({
  thread,
  asked,
  since,
  word,
  onOpen,
}: {
  thread: DoorThreadRead;
  asked: string[];
  since: string | null;
  /** Overdue past its reminder, else how long it has waited (proposal 10). */
  word: StateWord | null;
  onOpen: (thread: DoorThreadRead) => void;
}) {
  const sent = shortDate(since);
  return (
    <button
      type="button"
      className={`${styles.row} ${styles.rowMuted}`}
      onClick={() => onOpen(thread)}
      data-testid="waiting-row"
      data-door-item={thread.thread_no}
      data-action="qcp_item_open"
      {...{ [itemTargetAttr(thread.thread_no)]: thread.thread_no }}
    >
      <span className={`${styles.sev} ${styles.sev_waiting}`}>Asked</span>
      <span className={styles.rowBody}>
        <span className={styles.rowTitle}>{thread.title}</span>
        <span className={styles.waitingLine}>
          Asked <strong>{asked.join(', ')}</strong>
          {sent && ` on ${sent}`} · no answer yet
          {word && (
            <>
              {' '}
              <StateWordPill word={word} />
            </>
          )}
        </span>
        <NoteLine thread={thread} />
        <span className={styles.chips}>
          <span className={styles.chip}>
            {THREAD_SEVERITY_LABEL[thread.severity] ?? thread.severity}
          </span>
          <span className={`${styles.chip} ${styles.chipAge}`}>
            {ageLabel(thread.created_at)} since it came in
          </span>
          <NudgedChip thread={thread} />
        </span>
      </span>
      <span className={styles.rowRight}>
        <ThreadRefLabel thread={thread} />
      </span>
    </button>
  );
}

export default function QualityTriagePage() {
  const {
    invState,
    userName,
    deliveryOutcomes,
    dataRequests,
    responses = [],
    answers = [],
    openAnswer,
    foundAnswers,
    readOnly = false,
  } = useQualityTriage();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = readListFilter(params);
  // The latest daily brief teaser — best-effort: hidden when the fetch fails
  // (e.g. a deploy without the reports feed or a viewer without signals:read).
  const [latestBrief, setLatestBrief] = useState<SignalReport | null>(null);

  useEffect(() => {
    let cancelled = false;
    signalsReportsApi
      .list({ limit: 1 })
      .then((r) => {
        if (!cancelled) setLatestBrief(r.items[0] ?? null);
      })
      .catch(() => {
        /* strip stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const now = new Date();
  const seen = seenSnapshot();
  const requests = dataRequests?.requests ?? [];
  // The one list (proposal 4): every heading count, its cards, the rail and
  // the pager read it, so they cannot disagree.
  const list = triageList(invState.threads, invState.investigations, userName, requests, now);
  const lanes = { decide: list.yourCall, watching: list.watching };
  const away = routedAwayThreads(invState.threads, userName);
  // Open cases whose next step is on our side: the other half of "what needs
  // me", which used to live only on the Cases tab while this page said
  // "Nothing waiting on you". Read off the Cases board's one list, so this
  // count and the Cases tab's "Our court" column are one number (proposal 5).
  const ourCases = list.ourCases;
  // Routed away sits in the "More" drop-down now, so a send that never
  // arrived is also said here, where the reader lands (tabs round).
  const undelivered = deliveryOutcomes ? undeliveredCount(away, deliveryOutcomes) : 0;

  // Waiting on someone else: past their date first (the filter's order too);
  // an item with an open ask waits on whoever was asked.
  const waiting = list.waiting;
  const people = peopleWaitedOn(waiting.map((t) => waitingWho(t, requests)));
  const shownWaiting = filter.who
    ? waiting.filter((t) => namesInclude(waitingWho(t, requests), filter.who as string))
    : waiting;
  // My items someone has answered on, newest answer first.
  const mineIds = new Set([...list.yourCall, ...list.waiting, ...list.watching].map((t) => t.id));
  const responseOf = (t: DoorThreadRead) => noticeForThread(responses, t)?.response ?? null;
  // Nudge (kanban 68cc2cfb): the people a waiting item can be nudged about
  // are everyone it waits on; one that has been answered offers none. The
  // outcome lands in the shell's state so "nudged <date>" shows at once.
  const nudge = useNudge('triage', {
    thread: invState.applyThreadUpdate,
    request: dataRequests?.applyUpdate,
  });
  const targetsOf = (t: DoorThreadRead): NudgeTarget[] =>
    responseOf(t) ? [] : waitingTargets(t, requests, waitingWho(t, requests));
  // The one person the list is narrowed to, as each item names them.
  const personTargets: NudgeTarget[] = filter.who
    ? shownWaiting.flatMap((t) =>
        targetsOf(t).filter((target) => sameName(target.who, filter.who as string)),
      )
    : [];
  const responded = responses
    .filter((n) => n.kind === 'thread' && n.thread && mineIds.has(n.thread.id))
    .map((n) => n.thread as DoorThreadRead);
  // Everything "All my items" lists: the cards of every lane on the page.
  const mineCount = list.all.length;
  // Cases waiting on someone, for the pointer under the waiting filter.
  const casesWaiting = caseColumns(invState.investigations).theirs.length;

  // An opened item offers previous / next (case round, finding 6) through
  // the cards it was opened from: its lane on the full page (proposal 4: the
  // pager said "1 of 3" under "Waiting on your call 1"), or the filtered list.
  const filteredOrder =
    filter.show === 'waiting'
      ? shownWaiting.map((t) => t.thread_no)
      : responded.map((t) => t.thread_no);
  const search = withListFilter(params, filter);
  // A real URL per thread: the back button returns here (filtered as it
  // was), and the link can be bookmarked or pasted (Phase 2).
  const openIn = (order: string[]) => (thread: DoorThreadRead) =>
    navigate(threadWorkspacePath(thread), fromTab('triage', order, search));
  const openThread = openIn(filteredOrder);
  const setFilter = (f: ListFilter) => {
    trackEvent('filter', 'qcp_triage_filter', { show: f.show, who: f.who });
    setParams(new URLSearchParams(withListFilter(params, f)));
  };

  // Answers for you (proposal 1): Read opens the shell's drawer; Mark seen
  // and Attach change only the one answer.
  const [answerBusy, setAnswerBusy] = useState<string | null>(null);
  const [answerError, setAnswerError] = useState<string | null>(null);
  const readAnswer = (id: string) => {
    if (openAnswer) return openAnswer(id);
    const next = new URLSearchParams(params);
    next.set('answer', id);
    setParams(next);
  };
  const changeAnswer = async (id: string, work: () => Promise<DoorDataRequestRead>) => {
    setAnswerBusy(id);
    setAnswerError(null);
    try {
      dataRequests?.applyUpdate(await work());
      // The row (and the button that had focus) goes away: focus the next
      // answer, else the first item, never the page body (proposal 9).
      window.setTimeout(() => focusFirst([ANSWER_READ_SELECTOR, DOOR_ITEM_SELECTOR]), 0);
    } catch {
      setAnswerError('That did not go through. Try again in a moment.');
    } finally {
      setAnswerBusy(null);
    }
  };
  const answersList = (
    <>
      {foundAnswers?.available && !readOnly && (
        <FoundAnswers
          rows={foundAnswers.rows}
          asks={requests}
          onChanged={(updated) => {
            foundAnswers.applyUpdate(updated);
            // A confirmed ask is now answered: refresh the asks so the
            // Responded list and the badges pick it up without waiting
            // for the 30s poll.
            if (updated.status === 'retired') dataRequests?.refresh();
          }}
        />
      )}
      <AnswersForYou
        rows={answers}
        investigations={invState.investigations}
        userName={userName}
        busyId={answerBusy}
        onRead={readAnswer}
        onMarkSeen={(id) => void changeAnswer(id, () => markDataRequestSeen(id))}
        onAttach={(id, invId) => void changeAnswer(id, () => attachDataRequest(id, invId))}
      />
      {answerError && (
        <p className={styles.emptyNote} role="alert">
          {answerError}
        </p>
      )}
    </>
  );

  const chip = (label: string, count: number, on: boolean, f: ListFilter) => (
    <FilterChip
      key={label}
      label={label}
      count={count}
      on={on}
      action="qcp_triage_filter"
      recordValue="show"
      value={f.who ? 'waiting_on_person' : f.show}
      onClick={() => setFilter(f)}
    />
  );

  const waitingList = (rows: DoorThreadRead[], order: string[]) =>
    rows.map((t) => (
      <NudgeRow key={t.id} thread={t} targets={targetsOf(t)} nudge={nudge}>
        <WaitingRow
          thread={t}
          now={now}
          onOpen={openIn(order)}
          response={responseOf(t)}
          requests={requests}
        />
      </NudgeRow>
    ));

  return (
    <div className={styles.page}>
      {undelivered > 0 && (
        <p className={styles.undeliveredNotice} role="alert" data-testid="triage-undelivered">
          <span className={styles.notDeliveredWord}>{NOT_DELIVERED_WORDS}</span>{' '}
          {undelivered === 1 ? '1 item you sent' : `${undelivered} items you sent`} never
          reached the person. <Link to="routed">See Routed away →</Link>
        </p>
      )}
      {/* Only a pointer to the brief, not its text: the summary is written in
          the brief agent's shorthand ("Board flat at 5 (day 7)", "0 minted")
          that no first-time reader could follow, and it led the page. */}
      {latestBrief && (
        <p className={styles.footNote} style={{ marginTop: 0, marginBottom: 'var(--space-4)' }}>
          Merlin’s daily brief for {latestBrief.report_date} is ready ·{' '}
          {/* That day's brief itself, in a new tab the way Daily briefs opens
              every report; it used to land on the list (fan-out finding 11). */}
          <a
            href={signalsReportsApi.viewPath(latestBrief.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            open it ↗
          </a>
          {' · '}
          {/* Relative to the door, NOT "../briefs": this page is the door's
              index route, so ".." resolved past the door to /portal/briefs
              (a 404 on prod, 2026-09-08). */}
          <Link to="briefs">all briefs</Link>
        </p>
      )}

      {!invState.available && (
        <p className={styles.emptyNote}>
          Quality triage isn't enabled on this deploy — Ask Merlin (top right)
          still works.
        </p>
      )}

      <div className={styles.casesLayout}>
        {/* The same filter rail as Cases (waiting filter round): one control
            family for "show me what is waiting on someone". */}
        <aside className={styles.filterRail} aria-label="Triage filters">
          <div className={styles.filterGroup}>
            <div className={styles.filterHead}>Show</div>
            {chip('All my items', mineCount, filter.show === 'all', { show: 'all', who: null })}
            {chip('Waiting on others', waiting.length, filter.show === 'waiting' && !filter.who, {
              show: 'waiting',
              who: null,
            })}
            {chip('Responded', answers.length, filter.show === 'responded', {
              show: 'responded',
              who: null,
            })}
          </div>
          {/* Each person and how many items wait on them — one person too
              (Scott, 2026-10-06: "the number you have that you're waiting
              on for Laura"), so the count has a name on it. */}
          {people.length > 0 && (
            <div className={styles.filterGroup}>
              <div className={styles.filterHead}>Waiting on</div>
              {people.map((p) =>
                chip(p.who, p.count, !!filter.who && namesInclude([p.who], filter.who), {
                  show: 'waiting',
                  who: p.who,
                }),
              )}
            </div>
          )}
          {filter.show !== 'all' && (
            <button
              type="button"
              className={styles.filterClear}
              data-action="qcp_triage_filter_clear"
              onClick={() => setFilter({ show: 'all', who: null })}
            >
              Clear filter
            </button>
          )}
          {/* Items sent on to someone else: no longer mine to decide, so not a
              filter here, but the place to see them was a run-on footer
              line under the lanes and the "More" menu (UX round 2026-10-06). */}
          {away.length > 0 && (
            <div className={styles.filterGroup}>
              <div className={styles.filterHead}>Sent on</div>
              <Link
                to="routed"
                className={styles.filterChip}
                data-testid="rail-routed"
                data-action="qcp_rail_routed_away"
                onClick={() => trackEvent('navigation', 'qcp_rail_routed_away', { count: away.length })}
              >
                Routed away
                <span className={styles.filterCount}>{away.length}</span>
              </Link>
            </div>
          )}
        </aside>

        <div className={styles.listMain}>
          <SkipToItems list="triage" />
          {/* Answers lead the Responded and Overdue views; on the full list the
              items waiting on a decision come first (UX round 2026-10-27:
              the one decision sat below 17 answer cards). */}
          {(filter.show === 'responded' || filter.show === 'overdue') && answersList}
          {filter.show === 'waiting' && (
            <>
              <p className={styles.laneSub} style={{ marginTop: 0 }}>
                {filter.who ? (
                  <>
                    Your items waiting on <strong>{filter.who}</strong>.
                  </>
                ) : (
                  'Your items that cannot move until someone else answers.'
                )}{' '}
                One past its date comes first, marked overdue.
              </p>
              {filter.who && (
                <NudgePerson who={filter.who} targets={personTargets} nudge={nudge} />
              )}
              {shownWaiting.length === 0 ? (
                <div className={styles.emptyNote} data-testid="waiting-empty">
                  <p style={{ margin: '0 0 6px' }}>
                    {filter.who ? `Nothing of yours is waiting on ${filter.who}.` : 'Nothing is waiting on anyone else right now.'}
                  </p>
                  <p style={{ margin: 0 }}>
                    An item lands here when you send someone an ask from it. To mark one by
                    hand: open it, click its status next to the severity (for example
                    “Escalating”), and under “Or it is waiting on someone else” name the person.
                  </p>
                </div>
              ) : (
                waitingList(shownWaiting, filteredOrder)
              )}
              {casesWaiting > 0 && (
                <p className={styles.footNote}>
                  {casesWaiting === 1 ? '1 case is' : `${casesWaiting} cases are`} waiting on someone too ·{' '}
                  <Link to="cases?show=waiting">see Cases →</Link>
                </p>
              )}
            </>
          )}

          {filter.show === 'responded' && (
            <>
              {responded.length > 0 && (
                <p className={styles.laneSub} style={{ marginTop: 0 }}>
                  Your items where someone answered: a person you marked it as waiting on, or
                  someone who answered a question Merlin asked for you. Newest first.
                </p>
              )}
              {responded.length === 0 ? (
                answers.length === 0 && (
                  <p className={styles.emptyNote}>No answers are waiting for you to read.</p>
                )
              ) : (
                responded.map((t) =>
                  waitingOn(t) ? (
                    <WaitingRow
                      key={t.id}
                      thread={t}
                      now={now}
                      onOpen={openThread}
                      response={responseOf(t)}
                      requests={requests}
                    />
                  ) : (
                    <ThreadRow
                      key={t.id}
                      thread={t}
                      unseen={hasUnseenUpdate(t, seen)}
                      onOpen={openThread}
                      response={responseOf(t)}
                    />
                  ),
                )
              )}
            </>
          )}

          {filter.show === 'all' && (
            <>
              <h2 className={styles.lane} style={{ marginTop: 0 }}>
                Waiting on your call
                {lanes.decide.length > 0 && (
                  <span className={styles.lanePill}>{lanes.decide.length}</span>
                )}
              </h2>
              <p className={styles.laneSub}>
                {/* The line also promised three choices; an item offers four
                    (UX round 2026-10-20), and the item says what each does. */}
                Merlin flagged these for you and is holding them.
              </p>
              {lanes.decide.length === 0 && (
                <p className={styles.emptyNote}>Nothing waiting on you right now.</p>
              )}
              {lanes.decide.map((t) => (
                <ThreadRow
                  key={t.id}
                  thread={t}
                  unseen={hasUnseenUpdate(t, seen)}
                  onOpen={openIn(list.order.yourCall)}
                  response={responseOf(t)}
                />
              ))}

              {answersList}

              <h2 className={styles.lane}>
                Open cases where the next step is ours
                {ourCases.length > 0 && <span className={styles.lanePill}>{ourCases.length}</span>}
              </h2>
              <p className={styles.laneSub}>
                Cases already opened, with something still to do on our side. Open one to see
                what is outstanding and who owes it.
              </p>
              {ourCases.length === 0 && (
                <p className={styles.emptyNote}>No open case is waiting on our side.</p>
              )}
              {ourCases.map((inv) => {
                const progress = checklistProgress(inv);
                const caseAsks = asksForCase(requests, inv, invState.threads.find((t) => t.investigation_id === inv.id) ?? null);
                const asked = waitingOnNames(caseAsks);
                const askState = asked.length > 0 ? askWord(caseAsks, now) : null;
                return (
                  <CaseListRow
                    key={inv.id}
                    investigation={inv}
                    badge="CASE"
                    onOpen={() => navigate(caseWorkspacePath(inv), fromTab('triage', list.order.ourCases, search))}
                  >
                    {progress && (
                      <span className={styles.rowSub}>
                        Checklist: {progress.done} of {progress.total} done
                      </span>
                    )}
                    {asked.length > 0 && (
                      <span className={styles.caseWaiting}>
                        Asked {asked.join(', ')}, no answer yet
                        {askState && (
                          <>
                            {' '}
                            <StateWordPill word={askState} />
                          </>
                        )}
                      </span>
                    )}
                  </CaseListRow>
                );
              })}

              {/* Still mine, but not my move until someone answers: their own
                  lane, quieter rows, not in the "your call" count. */}
              {waiting.length > 0 && (
                <>
                  <h2 className={styles.lane}>
                    Waiting on someone else
                    <span className={`${styles.lanePill} ${styles.lanePillMuted}`}>{waiting.length}</span>
                  </h2>
                  <p className={styles.laneSub}>
                    Still yours, but each can only move once the person named answers. Past
                    its date it says Overdue; answered says Answered.
                  </p>
                  {waitingList(waiting, list.order.waiting)}
                </>
              )}

              <h2 className={styles.lane}>
                Watching
                {lanes.watching.length > 0 && (
                  <span className={styles.lanePill}>{lanes.watching.length}</span>
                )}
              </h2>
              <p className={styles.laneSub}>
                Nothing for you to decide — Merlin is tracking these and will bump them
                up if they move.
              </p>
              {lanes.watching.length === 0 && (
                <p className={styles.emptyNote}>Merlin isn't watching anything quietly.</p>
              )}
              {lanes.watching.map((t) => (
                <ThreadRow
                  key={t.id}
                  thread={t}
                  unseen={hasUnseenUpdate(t, seen)}
                  onOpen={openIn(list.order.watching)}
                  response={responseOf(t)}
                />
              ))}

            </>
          )}
        </div>
      </div>
    </div>
  );
}
